import type { ApiNode, NodeQuery } from '@goalbuddy/client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { api } from './api';

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 5 * 60_000 });
export const useUsers = () => useQuery({ queryKey: ['users'], queryFn: api.users, staleTime: 5 * 60_000 });
export const useSettings = () => useQuery({ queryKey: ['settings'], queryFn: api.settings });
export const useNodes = (q: NodeQuery, enabled = true) =>
  useQuery({ queryKey: ['nodes', q], queryFn: () => api.nodes(q), enabled });
export const useNode = (id: number | null) =>
  useQuery({ queryKey: ['node', id], queryFn: () => api.node(id!), enabled: id !== null });

/** All active structural nodes, used for the sidebar tree, parent pickers and breadcrumbs. */
export const useStructure = () =>
  useQuery({
    queryKey: ['nodes', 'structure'],
    queryFn: () => api.nodes({ limit: 1000 }),
    staleTime: 30_000,
  });

export function useUpdateNode() {
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & Parameters<typeof api.updateNode>[1]) =>
      api.updateNode(id, patch),
  });
}
export const useDone = () => useMutation({ mutationFn: (id: number) => api.doneNode(id) });
export const useReopen = () =>
  useMutation({ mutationFn: (id: number) => api.updateNode(id, { status: 'active' }) });

/** The detail panel is opened with `?n=<id>` on any page. */
export function useDetail() {
  const [params, setParams] = useSearchParams();
  const openId = params.get('n') ? Number(params.get('n')) : null;
  const open = useCallback(
    (id: number | null) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (id === null) next.delete('n');
          else next.set('n', String(id));
          return next;
        },
        { replace: false },
      ),
    [setParams],
  );
  return { openId, open };
}

export function userName(users: { id: number; name: string }[] | undefined, id: number | null) {
  if (id === null) return null;
  return users?.find((u) => u.id === id)?.name ?? `#${id}`;
}

export type { ApiNode };
